import UseCasePage, {
  type UseCaseFaq,
  type UseCaseScene,
  type UseCaseStat,
  type UseCaseWhy,
} from "@/components/public/use-case-page"
import type { SeoMeta } from "@/types"

const WHY: UseCaseWhy[] = [
  {
    title: "Walkthroughs answer \"would I live here?\" in 30 seconds",
    description: "Photo carousels show the house. A walkthrough shows the flow \u2014 how rooms connect, how big the garden feels, where the light comes from. For buyers scrolling in bed at 10pm, flow is what triggers a viewing request.",
  },
  {
    title: "Purpose-built for vertical feed viewing",
    description: "Most portal walkthroughs are 3-minute landscape tours that die on Instagram. QuiteRight's walkthrough template is built natively for 9:16 \u2014 captions, pacing, music and scene length all tuned for phone-first viewing.",
  },
  {
    title: "Cheaper than a videographer, at scale",
    description: "A cinematic walkthrough from a property videographer is \u00A3300+ per listing and takes a week. QuiteRight builds a walkthrough-style video from the existing photos in 60 seconds \u2014 perfect for every listing, not just the flagships.",
  },
  {
    title: "Works whether you have video clips or only stills",
    description: "If you have real walkthrough clips, QuiteRight can splice them into the template. If you only have portal stills, the Ken Burns engine produces a convincing walk-the-house feel by sequencing rooms in visiting order.",
  },
]

const SCENES: UseCaseScene[] = [
  { title: "\"Step inside\" opener", duration: "0:00\u20130:03", description: "Hero exterior frame with a soft pan-and-zoom. Text overlay primes the viewer: \u201Cstep inside \u2013 [address].\u201D Voiceover sets the location context." },
  { title: "Entrance and hallway", duration: "0:03\u20130:08", description: "First internal frame \u2014 usually the hallway or entrance. Sets the tone, shows the light, establishes the feel. Short caption on square footage or style cue." },
  { title: "Main living space", duration: "0:08\u20130:15", description: "The reception, kitchen-diner, or lounge \u2014 whichever is the property\u2019s strongest room. Slow camera-feel zoom. Caption highlights one lifestyle cue (e.g. \u201Copen-plan kitchen-diner\u201D)." },
  { title: "Bedrooms and secondary spaces", duration: "0:15\u20130:22", description: "Two or three quick cuts through the bedrooms. Voiceover keeps it tight: number of beds, en-suite count, garden or parking highlight." },
  { title: "Garden / final exterior", duration: "0:22\u20130:27", description: "Ends outside \u2014 garden, parking, or street-facing exterior. Completes the \u201Cwalk the property\u201D loop so the viewer feels they\u2019ve seen it end to end." },
  { title: "CTA close", duration: "0:27\u20130:30", description: "\u201CBook a viewing\u201D with phone and DM. Voiceover delivers the ask without over-selling \u2014 walkthrough has already done the work." },
]

const STATS: UseCaseStat[] = [
  { value: "30s", label: "ideal runtime", detail: "Long enough to feel like a walk. Short enough that buyers watch to the end." },
  { value: "9:16", label: "phone-first", detail: "Native vertical. No black bars, no re-cropping for Reels or TikTok." },
  { value: "60s", label: "build time", detail: "From listing URL to finished walkthrough ad \u2014 even when you've got no filmed clips." },
  { value: "3\u00D7", label: "avg engagement", detail: "Vs a static photo post \u2014 the walkthrough format consistently outperforms in UK property social." },
]

const FAQ: UseCaseFaq[] = [
  {
    q: "Do I need to film a real walkthrough?",
    a: "No. QuiteRight can produce a walkthrough-style video from the existing portal photos by sequencing rooms in visiting order with natural camera-feel motion and pacing. If you do have real footage, you can upload it and QuiteRight will splice it into the template.",
  },
  {
    q: "How long should a property walkthrough video be?",
    a: "25\u201335 seconds for social, 60\u201390 seconds for your listing page or YouTube. QuiteRight renders both: a short-form vertical for Reels/TikTok and a longer 16:9 version for embedded use.",
  },
  {
    q: "Should the walkthrough show every room?",
    a: "No. Five to seven rooms maximum, in visiting order \u2014 the strong ones. Showing every bedroom and every bathroom dilutes the video. The goal is to make the viewer feel they understand the property, not tick off an inventory.",
  },
  {
    q: "What about rooms with poor photos?",
    a: "Skip them. The walkthrough only needs to feature the strong frames. If the spare bedroom photo is weak, leave it out \u2014 the buyer will see it at the viewing. Never let a bad frame weaken a strong piece of marketing.",
  },
  {
    q: "Can I use this alongside a Matterport tour?",
    a: "Yes, and you should. The QuiteRight walkthrough is the social-first version: short, punchy, designed to stop a scroll. The Matterport tour is the high-intent version, embedded on the listing page for serious buyers. Different audiences, same listing.",
  },
]

export default function PropertyWalkthroughVideo({ meta }: { meta: SeoMeta }) {
  return (
    <UseCasePage
      meta={meta}
      useCaseName="Property Walkthrough Video"
      breadcrumbName="Property Walkthrough Video"
      heroHeadline="A phone-native walkthrough for every listing, not just the flagships"
      heroSubhead="A 30-second property walkthrough video is the fastest way to answer \u201Cwould I actually live here?\u201D in feed. QuiteRight builds one for every listing \u2014 even without a videographer on site \u2014 using the photos already on Rightmove, Zoopla or OnTheMarket."
      whyHeadline="Why walkthrough videos convert scrolls into viewings"
      whySubhead="Photos show the rooms. Walkthroughs show the flow. Flow is what triggers a booking."
      whyItems={WHY}
      anatomyHeadline="Anatomy of a walkthrough video"
      anatomySubhead="The QuiteRight walkthrough template, auto-sequenced from your listing photos."
      scenes={SCENES}
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Give every listing a walkthrough it deserves"
      ctaBody="No film crew, no shoot day, no week of editing \u2014 just a phone-native walkthrough video on every listing, every time."
      relatedKey="use-cases/property-walkthrough-video"
    />
  )
}
